/**
 * Centralized, hierarchical React Query key factory. Lives in `shared/api` because keys are
 * pure data, imported downward by features and by the socket-sync layer.
 *
 * Domains are migrated here incrementally; until a domain moves, its keys remain in the
 * legacy `src/api/queryKeys.ts` (`QK`), which aliases to these factories as they land so
 * every call site resolves to the SAME value during the transition (no cache split-brain).
 */
export { stableFilters } from './_filters';
export { presetKeys } from './presets.keys';
export { statusKeys, STATUS_TASK_BACKLINKS_ROOT } from './status.keys';
export {
  documentKeys,
  isDocumentListKey,
  DOCUMENT_DETAIL_ROOT,
  DOCUMENT_EDITOR_SEGMENT,
} from './documents.keys';
export { meetingKeys, MEETING_BACKLINKS_ROOT } from './meetings.keys';
export { companyKeys } from './company.keys';
export { auditKeys } from './audit.keys';
export { templateKeys } from './templates.keys';
export { adminKeys } from './admin.keys';
export {
  dataAnalysisKeys,
  DATA_ANALYSIS_ROOT,
  VIEW_ANNOTATIONS_SEGMENT,
  VALIDATION_VIEW_PAGE_SEGMENT,
} from './data-analysis.keys';
export { publicDocumentKeys } from './public-document.keys';
export { miscKeys } from './misc.keys';
export {
  requestKeys,
  REQUEST_CONTAINER_ROOT,
  REQUEST_BLOCK_ROOT,
  REQUEST_BACKLINKS_ROOT,
} from './request-flow.keys';

import { presetKeys } from './presets.keys';
import { statusKeys } from './status.keys';
import { documentKeys } from './documents.keys';
import { meetingKeys } from './meetings.keys';
import { requestKeys } from './request-flow.keys';
import { companyKeys } from './company.keys';
import { auditKeys } from './audit.keys';
import { templateKeys } from './templates.keys';
import { adminKeys } from './admin.keys';
import { dataAnalysisKeys } from './data-analysis.keys';
import { publicDocumentKeys } from './public-document.keys';
import { miscKeys } from './misc.keys';

// Union of every key this factory can produce — for typing generic helpers and the
// socket-sync invalidation predicates. Extend the tuple as domains migrate.
const _keyFactories = [
  ...Object.values(presetKeys),
  ...Object.values(statusKeys),
  ...Object.values(documentKeys),
  ...Object.values(meetingKeys),
  ...Object.values(requestKeys),
  ...Object.values(companyKeys),
  ...Object.values(auditKeys),
  ...Object.values(templateKeys),
  ...Object.values(adminKeys),
  ...Object.values(dataAnalysisKeys),
  ...Object.values(publicDocumentKeys),
  ...Object.values(miscKeys),
] as const;
export type AppQueryKey = ReturnType<(typeof _keyFactories)[number]>;
